import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import {
	STEWART_AUTH_SUCCESS_PORTAL_POST_MESSAGE,
	STEWART_CLOSE_PORTAL_POST_MESSAGE,
} from 'src/app/private/stewart-callback/stewart-callback.component';
import { environment } from 'src/environments/environment';
import * as crypto from 'crypto-js';
import { envTypes } from 'src/environments/env.types';
import { Logger } from '@core/logger';

export interface StewartAuthVerifierState {
	code_verifier: string;
	state: string;
}

const isDev = environment.env === envTypes.DEVELOPMENT;

@Component({
	selector: 'app-stewart-oauth-login',
	templateUrl: './stewart-oauth-login.component.html',
	styleUrls: ['./stewart-oauth-login.component.scss'],
	standalone: false,
})
export class StewartOAuthLoginComponent {
	public iframeUrl;

	constructor(
		private sanitizer: DomSanitizer,
		private dialogRef: MatDialogRef<StewartOAuthLoginComponent>,
	) {
		this.dialogRef.addPanelClass(['vw-90', 'vh-90']);
		Logger.info(
			`${StewartOAuthLoginComponent.name}: oauth login component activated.`,
		);

		const queryString = this.BuildStewartOAuthQueryString();

		const fullUrl = `${environment.stewartOAuth.stewartBaseLoginUrl}/connect/authorize?${queryString}`;

		if (!isDev) {
			this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
		} else {
			this.dialogRef.close(true);
			window.open(fullUrl, '_blank');
		}

		window.onmessage = async (ev: MessageEvent<unknown>) => {
			if (ev?.data === STEWART_CLOSE_PORTAL_POST_MESSAGE) {
				this.dialogRef.close(false);
			}

			if (ev?.data === STEWART_AUTH_SUCCESS_PORTAL_POST_MESSAGE) {
				this.dialogRef.close(true);
			}
		};
	}

	private BuildStewartOAuthQueryString() {
		const { authChallenge, codeChallenge } = this.generateAuthModel();
		const queryParams = {
			client_id: environment.stewartOAuth.stewartClientId,
			response_type: 'code',
			scope: environment.stewartOAuth.stewartAuthScopes,
			redirect_uri: encodeURIComponent(
				environment.stewartOAuth.stewartAuthRequestRedirectUri,
			),
			state: authChallenge.state,
			code_challenge: codeChallenge,
			code_challenge_method: 'S256',
		};

		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${queryParams[key]}`)
			.join('&');

		return queryString;
	}

	generateAuthModel(): {
		authChallenge: StewartAuthVerifierState;
		codeChallenge: string;
	} {
		const state = this.generateRandomString();
		const codeVerifier = this.generateRandomString();
		const codeChallenge = this.generateCodeChallenge(codeVerifier);

		const authChallenge: StewartAuthVerifierState = {
			code_verifier: codeVerifier,
			state: state,
		};

		localStorage.setItem(
			'stewart-title-verifier-state',
			JSON.stringify(authChallenge),
		);

		return { authChallenge, codeChallenge };
	}

	private generateRandomString(length: number = 128): string {
		const byteLength = Math.ceil(length / 2);

		const array = new Uint8Array(byteLength);
		window.crypto.getRandomValues(array);

		// Convert each byte to a hexadecimal string and join them
		const hexString = Array.from(array, (byte) =>
			byte.toString(16).padStart(2, '0'),
		).join('');

		// Return the substring of the desired length
		return hexString.slice(0, length);
	}

	private generateCodeChallenge(codeVerifier: string): string {
		return crypto.SHA256(codeVerifier).toString(crypto.enc.Base64url);
	}
}
